/**
 * The Home card for WhatsApp chats waiting in the review list (sketch:
 * docs/design/2026-10-12-realtor-whatsapp-capture-sketch.md §4).
 *
 * Shown only where the settings card is (the server has capture switched on
 * and this build carries the Meta config), while the connection is connected
 * and at least one chat waits. Hidden while the status loads and when it
 * fails to load.
 */

import { MessagesSquare } from "lucide-react";
import type { ReactElement } from "react";
import { useTranslation } from "react-i18next";
import { Link } from "react-router-dom";
import { WHATSAPP_REVIEW_PATH } from "@/features/realtor/components/settings/whatsapp-connection-card";
import { useWhatsappCapture } from "@/features/realtor/hooks/use-whatsapp";

const T = "realtor.dashboard.whatsappReview";

export function WhatsappReviewCard(): ReactElement | null {
	const { t } = useTranslation();
	const { connection } = useWhatsappCapture();
	if (connection?.status !== "connected" || connection.reviewEntriesLeft <= 0) {
		return null;
	}

	return (
		<section
			className="flex flex-col gap-3 rounded-xl border border-dashboard-border bg-dashboard-bg p-5 shadow-[var(--dashboard-shadow-2)] sm:flex-row sm:items-center"
			data-testid="whatsapp-review-card"
		>
			<div className="flex min-w-0 flex-1 items-start gap-3">
				<div className="shrink-0 rounded-full bg-dashboard-surface p-2">
					<MessagesSquare className="h-4 w-4 text-dashboard-accent-dark" />
				</div>
				<div className="min-w-0 space-y-0.5">
					<p className="text-sm font-semibold text-dashboard-text">
						{t(`${T}.title`, { count: connection.reviewEntriesLeft })}
					</p>
					<p className="text-xs text-dashboard-muted">{t(`${T}.body`)}</p>
				</div>
			</div>
			<Link
				to={WHATSAPP_REVIEW_PATH}
				className="inline-flex h-11 w-full items-center justify-center rounded-lg bg-dashboard-accent px-4 text-sm font-medium text-dashboard-on-fill hover:bg-dashboard-accent/90 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-dashboard-accent/40 sm:h-9 sm:w-auto"
			>
				{t(`${T}.review`)}
			</Link>
		</section>
	);
}
